import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { isJumioDoc } from 'utils/helpers';
import { usePaymentMethods, useSmallScreen, useUserRoleMatchCategory } from 'utils/hooks';
import { docTypes, investmentFunds, paymentMethods, roleCategories } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { DocumentStatus } from 'containers/dashboard/common-components';
import JumioReplaceIframe from './JumioReplaceIframe';
import { Box, Typography } from '@mui/material';

import DocumentTitle from './DocumentTitle';
import RIBValidationActions from './RIBValidationActions';
import RIBReplacementActions from './RIBReplacementActions';
import DocumentActionButtons from './DocumentActionButtons';
import CommentSection from './CommentSection';
import ValidationActions from './ValidationActions';

const Document = ({
    doc,
    isReplaceable,
    isRemovable,
    canValidate,
    canArchive,
    onChange,
    jumioStatus,
    useJumioIframe,
    onFinishReplace,
    displayValidation,
    fullName,
    boldName,
    belongsToCoSubscriber,
    replaceableRIB,
    withRIBValidation,
    subscription,
    hasDDA,
    isAudit,
}) => {
    const isSmallScreen = useSmallScreen('720');

    const [uploadedFile, setUploadedFile] = useState(null);
    const [replaceIframeVisible, setReplaceIframeVisible] = useState(false);
    const [showCommentLine, setShowCommentLine] = useState(false);

    const bankingStatement = subscription?.bankingStatement;
    const investmentFund = subscription?.investmentFund;
    const investingSum = subscription?.investmentCharacteristics?.investingSum;
    const subcategory = subscription?.investmentCharacteristics?.shareSubcategory;
    const mimeType = doc.mimeType || 'application/pdf';

    const paymentChoiceOptions = usePaymentMethods({
        subcategoryId: subcategory?.id,
        bankAccount: bankingStatement?.bankAccountNumber,
    });

    const isJumioType = useMemo(() => isJumioDoc(doc), [doc]);
    const isSecondId = doc.type === docTypes.SECONDARY_ID;
    const isFirstId = doc.type === docTypes.ID;
    const userIsInvestor = useUserRoleMatchCategory(roleCategories.INVESTORS);

    const canChangePaymentMethod =
        paymentChoiceOptions.length > 1 &&
        (subscription?.investmentFund?.name !== investmentFunds.PEQAN_CO ||
            bankingStatement?.paymentMethod !== paymentMethods.DIRECT_DEBIT ||
            investingSum >= 100000);

    const handleFinishJumioReplace = useCallback(() => {
        setReplaceIframeVisible(false);
        onFinishReplace?.();
    }, [onFinishReplace]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', mb: '15px' }}>
            <Box
                sx={theme => ({
                    display: 'flex',
                    flexWrap: isJumioType ? 'wrap' : 'noWrap',
                    gap: 2,
                    alignItems: 'center',
                    [theme.breakpoints.down(720)]: {
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                    },
                })}
            >
                <DocumentTitle
                    doc={doc}
                    fullName={fullName}
                    belongsToCoSubscriber={belongsToCoSubscriber}
                    boldName={boldName}
                    uploadedFile={uploadedFile}
                    isAudit={isAudit}
                    mimeType={mimeType}
                />
                {isReplaceable && uploadedFile && (
                    <Typography
                        sx={{ ml: '20px', '@media (max-width:720px)': { margin: 0 } }}
                        gutterBottom
                    >{`${texts.UPLOADED_FILE_NAME} : ${uploadedFile.name}`}</Typography>
                )}
                {withRIBValidation && (
                    <RIBValidationActions
                        bankingStatement={bankingStatement}
                        onFinishReplace={onFinishReplace}
                    />
                )}
                {replaceableRIB && (
                    <RIBReplacementActions
                        doc={doc}
                        bankingStatement={bankingStatement}
                        investmentFund={investmentFund}
                        hasDDA={hasDDA}
                        canChangePaymentMethod={canChangePaymentMethod}
                    />
                )}
                <DocumentActionButtons
                    doc={doc}
                    isReplaceable={isReplaceable}
                    canArchive={canArchive}
                    isSecondId={isSecondId}
                    isJumioType={isJumioType}
                    isFirstId={isFirstId}
                    userIsInvestor={userIsInvestor}
                    useJumioIframe={useJumioIframe}
                    setReplaceIframeVisible={setReplaceIframeVisible}
                    isRemovable={isRemovable}
                    isSmallScreen={isSmallScreen}
                    onChange={onChange}
                    setUploadedFile={setUploadedFile}
                />
                <ValidationActions
                    doc={doc}
                    displayValidation={displayValidation}
                    canValidate={canValidate}
                    setShowCommentLine={setShowCommentLine}
                />
                {isReplaceable && isJumioType && <DocumentStatus status={jumioStatus} />}
                {isAudit && (
                    <Typography sx={{ ml: '20px', fontStyle: 'italic' }}>
                        {texts.VALIDATED_BY(
                            doc.createdByName,
                            moment(doc.validatedAt).format('DD/MM/YYYY')
                        )}
                    </Typography>
                )}
            </Box>
            <Box display="flex" flexDirection="column">
                {replaceIframeVisible && (
                    <JumioReplaceIframe doc={doc} onFinish={handleFinishJumioReplace} />
                )}
                {showCommentLine && <CommentSection doc={doc} />}
            </Box>
        </Box>
    );
};

Document.propTypes = {
    doc: PropTypes.object,
    isReplaceable: PropTypes.bool,
    canArchive: PropTypes.bool,
    boldName: PropTypes.bool,
    onChange: PropTypes.func,
    jumioStatus: PropTypes.object,
    useJumioIframe: PropTypes.bool,
    onFinishReplace: PropTypes.func,
    displayValidation: PropTypes.bool,
    belongsToCoSubscriber: PropTypes.bool,
    replaceableRIB: PropTypes.bool,
    withRIBValidation: PropTypes.bool,
    subscription: PropTypes.shape({
        bankingStatement: PropTypes.object,
        investmentFund: PropTypes.object,
        investmentCharacteristics: PropTypes.object,
    }),
    isRemovable: PropTypes.bool,
    canValidate: PropTypes.bool,
    hasDDA: PropTypes.bool,
    isAudit: PropTypes.bool,
    fullName: PropTypes.string,
};

export default Document;
